import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ILollipop } from '../../chart-plugins/LollipopsOverlayPlugin';
import { ObjectUtil } from 'app/utils/object.util';
import { CustomNumberFormatPipe, EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import Highcharts from 'highcharts';
import { CS_AREA_CHART_OPTIONS, textStyle } from 'app/shared/helper/highchart-options';
import { cleanChart, getXDateFormat, updateChart } from 'app/shared/helper/highcharts.helper';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import { Router } from '@angular/router';
import { LayoutService } from 'app/shared/services/layout/layout.service';
import 'highcharts/highcharts-more';
import 'highcharts/modules/dumbbell';
import 'highcharts/modules/lollipop';

@Component({
  selector: 'app-performance-analyze-highchart',
  templateUrl: './performance-analyze-highchart.component.html',
  styleUrls: ['./performance-analyze-highchart.component.scss', '../performance-monitoring.component.scss'],
  standalone: false,
})
export class PerformanceAnalyzeHighChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() alarms: any;
  @Input() data: any;
  @Input() yAxesPowerMax: number;
  @Input() yAxesWindspeedMax: number;
  @Input() unit: string;
  @Input() rangeHours: number;
  @Input() haveLollipop: boolean;
  @Input() chartHeight = 220;
  @Input() alarmsVisible: boolean = true;

  numberOfTurbinesReceived: any;
  popupContent: any;
  popupTop: number;
  popupLeft: number;
  popupNumberOfTurbinesReceived = false;
  chartDatasets = [];
  chartColors = [];
  showAlarmPopup = null;
  popupAlarms = [];
  timezone: string;
  highcharts = Highcharts;
  hChartOptions: Highcharts.Options;
  chartRef: Highcharts.Chart;
  actual: number[] | undefined;
  theoretical: number[] | undefined;
  rated: number[] | undefined;
  timestamps: number[] | undefined;
  windSpeed: number[] | undefined;
  tooltipPosition: { x: number; y: number } = { x: 0, y: 0 };
  tooltipData: any = {};
  series: any[] | undefined;
  isTurbineLevel: boolean = false;
  private popupHideTimeout?: ReturnType<typeof setTimeout>;
  popupPinned = false;

  chartCallback: Highcharts.ChartCallbackFunction = chart => {
    this.chartRef = chart;
  };

  private unsubscribe$ = new Subject();
  private lollipops: ILollipop[] = [];
  popupVersion = 0;
  private activePopupKey: string | null = null;
  private lollipopPointerY = 0;
  private readonly lollipopHitRadius = 12;

  @ViewChild('pahighchart', { static: true }) pahighchart: ElementRef<HTMLDivElement>;
  @ViewChild('alarmPopup') alarmPopup?: ElementRef<HTMLElement>;

  constructor(
    private themeService: ThemeService,
    private translate: TranslateService,
    private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private router: Router,
    private timezoneService: TimezoneService,
    private dateTimeFormatService: DateTimeFormatService,
    private customNumberPipe: CustomNumberFormatPipe,
    private layoutService: LayoutService,
  ) {}

  ngOnInit() {
    this.isTurbineLevel = this.router.url.includes('turbine');
    fromEvent(document, 'scroll', { capture: true })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(event => this.onViewportChanged(event));
    this.subscribeOnThemeChange();
    this.subscribeOnTimezoneChange();
    this.layoutService.sidebarWidth$.pipe(takeUntil(this.unsubscribe$)).subscribe(width => {
      this.currentSidebarWidth = width;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.timezone = this.timezoneService.selectedOption;
    if (changes.data) {
      this.updateLollipops();
      this.updateSeries();
    }

    if (changes.alarmsVisible) {
      this.updateLollipops();
      this.updateSeries();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;

    if (this.showAlarmPopup && this.popupPinned && !this.alarmPopup?.nativeElement.contains(target)) {
      this.hidePopup();
    }
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('window:resize')
  onViewportChanged(event?: Event): void {
    if (!event || !this.isScrollInsidePopup(event)) {
      this.hidePopup();
    }
  }

  hidePopup(): void {
    this.clearPopupHideTimeout();

    this.showAlarmPopup = false;
    this.popupPinned = false;

    const previousPopupKey = this.activePopupKey;
    this.activePopupKey = null;
    this.applyLollipopHighlight(previousPopupKey, null);
  }

  private isScrollInsidePopup(event: Event): boolean {
    const target = event.target;

    return target instanceof Node && !!this.alarmPopup?.nativeElement.contains(target);
  }

  buildChartData() {
    const self = this;
    this.actual = ObjectUtil.getProperty(this.data, 'actualPower', null);
    this.theoretical = ObjectUtil.getProperty(this.data, 'theoreticalPower', null);
    this.rated = ObjectUtil.getProperty(this.data, 'ratedPower', null);
    this.timestamps = ObjectUtil.getProperty(this.data, 'timestamps', []);
    this.windSpeed = ObjectUtil.getProperty(this.data, 'windSpeed', []) || [];
    this.numberOfTurbinesReceived = ObjectUtil.getProperty(this.data, 'transmittingTurbines', null);
    this.series = this.getSeries();
    const yAxis = Highcharts.merge({}, CS_AREA_CHART_OPTIONS.yAxis);
    const timestamps = ObjectUtil.getProperty(this.data, 'timestamps', []);
    const windSpeed = ObjectUtil.getProperty(this.data, 'windSpeed', []) || [];

    this.hChartOptions = Highcharts.merge(CS_AREA_CHART_OPTIONS, {
      chart: {
        min: 0,
        height: this.chartHeight,
        spacing: [10, 5, 20, 5],
      },
      plotOptions: {
        ...CS_AREA_CHART_OPTIONS.plotOptions,
        series: {
          ...CS_AREA_CHART_OPTIONS.plotOptions.series,
          marker: {
            enabled: false,
            animation: false,
            symbol: 'circle',
            lineWidth: 0,
          },
        },
      },
      tooltip: {
        animation: {
          duration: 0,
        },
        hideDelay: 0,
        shared: true,
        useHTML: true,
        backgroundColor: 'transparent',
        formatter: function () {
          self.onHoverChart(this);
          const labelText = self.data?.totalTurbines
            ? `${self.translate.instant('PERFORMANCE_FACTS.DATA_FROM')} ${self.tooltipData?.subtitle} ${self.translate.instant('PERFORMANCE_FACTS.OUT_OF')} ${self.data?.totalTurbines} ${self.translate.instant('COMMON.TURBINES')}`
            : '';
          const x = self.tooltipData?.signals
            .map(el => {
              return (
                '<div class="text-primary font-size-small">' +
                el?.label +
                ': ' +
                '<span class="font-weight-bold tooltip-value font-size-small">' +
                self.customNumberPipe.transform(el?.value?.toFixed(2)) +
                ' ' +
                el?.unit +
                '</span>' +
                '</div>'
              );
            })
            .join('');
          return self.showAlarmPopup || !self.tooltipData?.signals?.length
            ? false
            : `
          <div class="popup-content">
            <div class="d-flex mb-2">
              <span class="font-weight-bold text-primary font-size-normal mr-3 tooltip-value">${self.tooltipData?.date}</span>
              <span class="ml-3 tooltip-value font-size-small label-text">
               ${labelText}
              </span>
            </div>
            ${x}
          </div>
          `;
        },
      },
      legend: {
        align: 'center',
        verticalAlign: 'top',
        layout: 'horizontal',
        padding: 5,
        itemStyle: {
          color: 'var(--font)',
          fontSize: '12px',
        },
        zIndex: 1,
        enabled: true,
        useHTML: true,
        symbolWidth: 0,
        symbolHeight: 0,
        labelFormatter: function () {
          const params = this as any;
          const seriesColor = params.userOptions.lineColor || params.userOptions.color;
          const backgroundColor = params.userOptions.fillColor;
          const seriesName = params.name;
          const markerHtml = `<span style="
            display: inline-block;
            width: 14px; height: 14px;
            border: 2px solid ${seriesColor};
            border-radius: 50%;
            background-color: ${backgroundColor};
            margin-right: 5px;
            vertical-align: middle;"></span>`;

          return `<div style="display: flex; align-items: center">${markerHtml} ${seriesName}</div>`;
        },
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          day: getXDateFormat('dm', this.dateTimeFormatService),
          hour: getXDateFormat('t', this.dateTimeFormatService),
          minute: getXDateFormat('t', this.dateTimeFormatService),
        },
        min: timestamps?.[0] - 1000 * 60 * 5,
      },
      yAxis: [
        {
          ...yAxis,
          tickAmount: 6,
          max: this.yAxesPowerMax,
          title: {
            text: this.unit || '',
            ...textStyle,
          },
          labels: {
            ...textStyle,
            formatter: function () {
              return this.value;
            },
          },
        },
        {
          ...yAxis,
          tickAmount: 6,
          opposite: true,
          visible: !!windSpeed.length,
          max: this.yAxesWindspeedMax,
          title: {
            text: 'm/s',
            ...textStyle,
          },
          labels: {
            ...textStyle,
            formatter: function () {
              return this.value;
            },
          },
        },
      ],
      time: {
        timezone: this.timezone,
      },
      series: this.series,
    } as Highcharts.Options);
  }

  getSeries() {
    return [
      {
        data: this.actual?.map((value, index) => [this.timestamps[index], value]),
        name: this.translate.instant('PERFORMANCE_MONITORING.ACTUAL'),
        type: 'area',
        dashStyle: 'Solid',
        yAxis: 0,
        stickyTracking: true,
        trackByArea: false,
        lineColor: this.themeService.getColor('--primary-background'),
        fillColor: `${this.themeService.getColor('--primary-background')}33`,
        unit: this.unit,
      },
      {
        data: this.theoretical?.map((value, index) => [this.timestamps[index], value]),
        name: this.translate.instant('PERFORMANCE_MONITORING.THEORETICAL'),
        type: 'area',
        dashStyle: 'Solid',
        yAxis: 0,
        stickyTracking: true,
        trackByArea: false,
        lineColor: this.themeService.getColor('--tertiary'),
        fillColor: `${this.themeService.getColor('--tertiary')}33`,
        unit: this.unit,
        marker: {
          fillColor: this.themeService.getColor('--tertiary'),
        },
      },
      {
        data: this.windSpeed?.map((value, index) => [this.timestamps[index], value]) || [],
        name: this.translate.instant('PERFORMANCE_MONITORING.WIND_SPEED'),
        visible: !!this.windSpeed.length,
        yAxis: 1,
        showInLegend: !!this.windSpeed.length,
        type: 'line',
        dashStyle: 'Solid',
        trackByArea: false,
        stickyTracking: true,
        unit: 'm/s',
        color: this.themeService.getColor('--budget-follow-up-delivered-scada'),
      },
      {
        data: this.rated?.map((value, index) => [this.timestamps[index], value]),
        name: this.translate.instant('COMMON.CHART_LEGEND_RATED_POWER'),
        type: 'line',
        showInLegend: !!this.rated?.length,
        hideInTooltip: true,
        stickyTracking: false,
        dashStyle: 'Dash',
        yAxis: 0,
        color: this.themeService.getColor('--gray100'),
        fillColor: 'transparent',
        state: {
          hover: {
            enabled: false,
          },
        },
        marker: {
          radius: 0,
          enabled: false,
          states: {
            enabled: false,
          },
          select: {
            enabled: false,
          },
        },
      },
      ...this.lollipops.map((lollipop, index) => {
        const color = this.getLollipopColor(lollipop.position);

        return {
          type: 'lollipop',
          id: `lollipop-${lollipop.position}`,
          showInLegend: false,
          hideInTooltip: true,
          visible: this.alarmsVisible,
          data: [
            {
              x: this.timestamps[lollipop.position],
              y: this.yAxesPowerMax,
              labelValue: lollipop.count < 99 ? lollipop.count : 'X',
            },
          ] as any,
          marker: {
            enabled: true,
            symbol: 'circle',
            radius: 8,
            fillColor: color,
            lineWidth: 0,
            states: {
              hover: {
                radiusPlus: 0,
                lineWidth: 0,
                lineWidthPlus: 0,
              },
            },
          },
          states: {
            hover: {
              halo: false,
              connectorWidthPlus: 0,
            },
          },
          point: {
            events: {
              click: event => this.onLollipopClick(lollipop, event),
            },
          },
          dataLabels: {
            enabled: true,
            color: 'white',
            crop: false,
            backgroundColor: color,
            borderRadius: 50,
            padding: 0,
            useHTML: true,
            overflow: 'allow',
            formatter: function () {
              const customTag = ((this as any).point.options as any).labelValue;
              return `<div style="width: 15px; height: 15px; padding-top: 1px">${customTag}</div>`;
            },
            style: {
              textOutline: 'none',
              fontWeight: 'normal',
              fontSize: '12px',
            },
            x: -2,
            y: -8,
            align: 'center',
            verticalAlign: 'top',
          },
          connectorWidth: 1,
          connectorColor: color,
          color: color,
          lineColor: color,
          stickyTracking: true,
        };
      }),
      ...this.lollipopArea(),
    ];
  }

  private lollipopArea() {
    return this.isTurbineLevel
      ? this.lollipops.map(lollipop => ({
          data: [
            [this.timestamps[lollipop.position], this.yAxesPowerMax],
            [this.timestamps[lollipop.endPosition], this.yAxesPowerMax],
          ],
          type: 'area',
          dashStyle: 'Solid',
          yAxis: 0,
          showInLegend: false,
          hideInTooltip: true,
          visible: this.alarmsVisible,
          lineColor: 'none',
          marker: {
            enabled: false,
            symbol: 'circle',
            radius: 0,
            fillColor: this.themeService.getColor('--red'),
            lineWidth: 0,
          },
          fillColor: `${this.themeService.getColor('--red')}66`,
          stickyTracking: false,
        }))
      : [];
  }

  private updateSeries() {
    cleanChart(this.chartRef);
    this.buildChartData();
    updateChart(this.chartRef, this.series);
  }

  public onChartMouseMove(event: MouseEvent): void {
    this.lollipopPointerY = event.clientY;
    this.updateLollipopHover(event);
  }

  public onChartClick(event: MouseEvent): void {
    if (!this.chartRef || !this.alarmsVisible) {
      return;
    }

    const lollipop = this.lollipops.find(item => this.isPointerNearLollipop(event, item));

    if (lollipop) {
      this.onLollipopClick(lollipop, event);
    }
  }

  private updateLollipopHover(event: MouseEvent): void {
    if (this.popupPinned || !this.chartRef || !this.alarmsVisible) {
      return;
    }

    const lollipop = this.lollipops.find(item => this.isPointerNearLollipop(event, item));

    if (lollipop) {
      this.cancelPopupHide();
      const plotX = this.chartRef.xAxis[0].toPixels(this.timestamps[lollipop.position], true);
      this.showLollipopPopup(plotX, lollipop);
    } else if (this.showAlarmPopup) {
      this.schedulePopupHide();
    }
  }

  private isPointerNearLollipop(event: MouseEvent, lollipop: ILollipop): boolean {
    const chartRect = this.pahighchart.nativeElement.getBoundingClientRect();
    const markerX =
      chartRect.left +
      this.chartRef.plotLeft +
      this.chartRef.xAxis[0].toPixels(this.timestamps[lollipop.position], true);
    const markerY = chartRect.top + this.chartRef.plotTop;

    return (
      Math.abs(event.clientX - markerX) <= this.lollipopHitRadius &&
      Math.abs(event.clientY - markerY) <= this.lollipopHitRadius
    );
  }

  private onHoverChart = event => {
    const index = event.index;

    this.tooltipData.date = this.dateTimeFormatPreferencePipe.transform(
      moment(this.timestamps?.[index]).toDate(),
      'dmyt',
      this.timezone,
    );

    this.tooltipData.subtitle = this.numberOfTurbinesReceived ? this.numberOfTurbinesReceived[index] : null;

    const visibleSeries = this.chartRef.series.filter(serie => serie.visible).map(serie => serie.name);

    const noLolipops = this.series.filter(
      signal => !signal.hideInTooltip && signal.data?.[index]?.[1] !== undefined && visibleSeries.includes(signal.name),
    );

    this.tooltipData.signals =
      noLolipops.map(signal => ({
        label: signal.name,
        value: signal.data[index][1],
        unit: signal.unit,
      })) || [];
  };

  private updateLollipops() {
    const timestamps = ObjectUtil.getProperty(this.data, 'timestamps', []);
    this.lollipops = [];
    if (this.alarms) {
      const firstTime = timestamps[0];
      const lastTime = timestamps[timestamps.length - 1];
      const deltaT = (lastTime - firstTime) / (timestamps.length - 1);
      const maxPosition = timestamps.length - 2;
      this.lollipops = this.alarms
        .filter(a => a.closeTS >= firstTime)
        .map((a: any) => {
          const calculatePosition = timestamp => Math.min(Math.floor((timestamp - firstTime) / deltaT), maxPosition);
          const position = a.createTS >= firstTime ? calculatePosition(a.createTS) : 0;
          const endPosition = this.haveLollipop ? undefined : a.closeTS ? calculatePosition(a.closeTS) : maxPosition;

          return {
            position,
            endPosition,
            alarm: a,
          };
        })
        .reduce((lollipops: ILollipop[], a) => {
          const lollipop = lollipops.find(l => l.position === a.position);
          if (lollipop) {
            lollipop.count++;
            lollipop.alarms.push(a.alarm);
            if (a.endPosition && (!lollipop.endPosition || a.endPosition > lollipop.endPosition)) {
              lollipop.endPosition = a.endPosition;
            }
          } else {
            lollipops.push({
              position: a.position,
              endPosition: a.endPosition,
              count: 1,
              alarms: [a.alarm],
            });
          }
          return lollipops;
        }, []);
    }
  }

  private showLollipopPopup(plotX: number, lollipop: ILollipop): void {
    const chartRect = this.pahighchart.nativeElement.getBoundingClientRect();

    const anchorX = chartRect.left + this.chartRef.plotLeft + plotX;

    const anchorY = chartRect.top + this.chartRef.plotTop;

    this.showAlarmPopupAt(anchorX, anchorY, lollipop.alarms, `chart-${lollipop.position}`);
  }

  private onLollipopClick(lollipop: ILollipop, event: any): void {
    (event?.originalEvent || event)?.stopPropagation?.();
    this.cancelPopupHide();
    this.showLollipopPopup(
      this.chartRef.xAxis[0].toPixels(this.timestamps[lollipop.position], true),
      lollipop,
    );
    this.popupPinned = true;
  }

  private subscribeOnThemeChange(): void {
    this.themeService
      .getTheme()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.updateSeries();
      });
  }

  private subscribeOnTimezoneChange() {
    this.timezoneService
      .getTimezoneObservable()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(tz => {
        this.timezone = tz.timezone;
        this.updateSeries();
      });
  }

  schedulePopupHide(): void {
    if (this.popupPinned) {
      return;
    }

    this.clearPopupHideTimeout();

    this.popupHideTimeout = setTimeout(() => {
      this.hidePopup();
    }, 250);
  }

  cancelPopupHide(): void {
    this.clearPopupHideTimeout();
  }

  private clearPopupHideTimeout(): void {
    if (this.popupHideTimeout) {
      clearTimeout(this.popupHideTimeout);
      this.popupHideTimeout = undefined;
    }
  }

  onPopupClicked(): void {
    this.popupPinned = true;
    this.cancelPopupHide();
  }

  private readonly safeMargin = 8;
  private currentSidebarWidth = 252;
  private readonly rightMargin = 100;
  private readonly popupEstimatedWidth = 500;
  private readonly popupTopGap = -25;

  private showAlarmPopupAt(anchorX: number, anchorY: number, alarms: any[], popupKey: string): void {
    const isNewPopup = !this.showAlarmPopup || this.activePopupKey !== popupKey;

    if (isNewPopup) {
      const previousPopupKey = this.activePopupKey;
      this.popupVersion++;
      this.activePopupKey = popupKey;
      this.applyLollipopHighlight(previousPopupKey, popupKey);
    }

    const minLeft = this.currentSidebarWidth + this.safeMargin;
    const centeredLeft = anchorX - this.popupEstimatedWidth / 2;
    const maxLeft = window.innerWidth - this.popupEstimatedWidth - this.rightMargin - this.safeMargin;

    this.popupLeft = Math.max(minLeft, Math.min(centeredLeft, maxLeft));
    this.popupTop = Math.max(this.safeMargin, anchorY - this.popupTopGap);

    this.popupAlarms = alarms;
    this.showAlarmPopup = true;
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  private getLollipopColor(position: number): string {
    return this.activePopupKey === `chart-${position}` ? this.getSelectedLollipopColor() : this.themeService.getColor('--red');
  }

  private getSelectedLollipopColor(): string {
    return this.themeService.getTheme().value === EThemeName.DARK ? '#EA868F' : '#842029';
  }

  private applyLollipopHighlight(previousPopupKey: string | null, currentPopupKey: string | null): void {
    if (!this.chartRef || previousPopupKey === currentPopupKey) {
      return;
    }

    const previousPosition = this.lollipopPositionFromPopupKey(previousPopupKey);
    const currentPosition = this.lollipopPositionFromPopupKey(currentPopupKey);

    if (previousPosition != null) {
      this.setLollipopSeriesColor(previousPosition, this.themeService.getColor('--red'));
    }

    if (currentPosition != null) {
      this.setLollipopSeriesColor(currentPosition, this.getSelectedLollipopColor());
    }
  }

  private lollipopPositionFromPopupKey(popupKey: string | null): number | null {
    if (!popupKey?.startsWith('chart-')) {
      return null;
    }

    const position = Number(popupKey.replace('chart-', ''));
    return Number.isNaN(position) ? null : position;
  }

  private setLollipopSeriesColor(position: number, color: string): void {
    const series = this.chartRef?.get(`lollipop-${position}`) as Highcharts.Series;

    series?.update(
      {
        color,
        lineColor: color,
        connectorColor: color,
        marker: { fillColor: color },
        dataLabels: { backgroundColor: color },
      } as Highcharts.SeriesOptionsType,
      true,
    );
  }
}
